import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Favorite as FavoriteIcon,
  HomeWork as ListingIcon,
  Search as SearchIcon,
  ShoppingCart as ProductIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import SearchResultCard from '../components/search/SearchResultCard';
import { useFavorites } from '../context/FavoritesContext';

const FAVORITE_TYPES = [
  { value: 'listing', label: 'favorites.types.listings', icon: ListingIcon },
  { value: 'product', label: 'favorites.types.products', icon: ProductIcon }
];

const SORT_OPTIONS = [
  { value: 'recent', label: 'favorites.sort.recent' },
  { value: 'oldest', label: 'favorites.sort.oldest' },
  { value: 'title', label: 'favorites.sort.title' },
  { value: 'price', label: 'favorites.sort.price' },
  { value: 'rating', label: 'favorites.sort.rating' }
];

const FavoritesPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { listFavorites, favoriteIds, loading: idsLoading } = useFavorites();

  const [favoriteType, setFavoriteType] = useState('listing');
  const [search, setSearch] = useState('');
  const [city, setCity] = useState('');
  const [sort, setSort] = useState('recent');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;

    const loadFavorites = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await listFavorites({
          type: favoriteType,
          search,
          city,
          sort,
          page,
          limit: 12
        });

        if (!active) return;

        setItems(Array.isArray(response?.data) ? response.data : []);
        setPagination(response?.pagination || null);
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.message || err?.message || 'Impossible de charger vos favoris.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadFavorites();

    return () => {
      active = false;
    };
  }, [favoriteType, search, city, sort, page, listFavorites]);

  useEffect(() => {
    setPage(1);
  }, [favoriteType, search, city, sort]);

  const totalFavorites = useMemo(() => {
    if (favoriteType === 'product') {
      return favoriteIds.products.length;
    }
    return favoriteIds.listings.length;
  }, [favoriteIds, favoriteType]);

  const handleCardSelect = (item) => {
    if (favoriteType === 'product') {
      navigate(`/product/${item.id}/order`);
      return;
    }

    navigate(`/view-listing/${item.id}`);
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', py: { xs: 2, md: 4 } }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              border: '1px solid #e9e9e9',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.12)} 100%)`
            }}
          >
            <Box sx={{
            mb: 3,
            p: { xs: 2, md: 3 },
            bgcolor: 'background.paper',
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden',
            borderStyle: 'solid',
            borderWidth: '6px 0px 0px 0px',
            borderImage: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%) 1',
            boxShadow: (theme) => `0 8px 20px rgba(98,0,238,0.22), 0 18px 40px rgba(3,218,196,0.16)`,
            boxSizing: 'border-box'
          }}>
              <Stack spacing={2}>
                <Stack direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="space-between" alignItems={isMobile ? 'flex-start' : 'center'}>
                  <Box>                   
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                      {t('common.favorites', 'Favoris')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {t('favorites.description', 'Gérez votre sélection personnelle de listings, services et produits marketplace.')}
                    </Typography>
                  </Box>
                  <Card elevation={0} sx={{ 
                    borderRadius: 3, 
                    minWidth: isMobile ? '100%' : 220,
                    background: `linear-gradient(135deg, ${alpha('#6200ee', 0.08)} 0%, ${alpha('#03dac4', 0.1)} 100%)`,
                    border: '1.5px solid',
                    borderColor: alpha('#6200ee', 0.24),
                    boxShadow: `0 8px 16px ${alpha('#6200ee', 0.12)}`,
                    transition: 'all 0.3s ease'
                  }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {t('favorites.totalSaved', 'Total enregistré')}
                      </Typography>
                      <Typography variant="h4" sx={{ 
                        fontWeight: 800, 
                        mt: 0.5,
                        backgroundImage: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}>
                        {idsLoading ? '...' : totalFavorites}
                      </Typography>
                    </CardContent>
                  </Card>
                </Stack>

                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      p: '8px',
                      borderRadius: '14px',
                      background: theme.palette.background.paper,
                      border: '1px solid #e9e9e9',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                      gap: '4px'
                    }}
                  >
                    {FAVORITE_TYPES.map(({ value, label, icon: Icon }) => {
                      const active = favoriteType === value;
                      return (
                        <Box
                          key={value}
                          onClick={() => setFavoriteType(value)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.875,
                            px: { xs: 2.5, md: 3.5 },
                            py: { xs: 1, md: 1.125 },
                            borderRadius: '10px',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            border: '1.5px solid transparent',
                            position: 'relative',
                            ...(active ? {
                              background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                              color: 'white',
                              boxShadow: '0 8px 20px rgba(98,0,238,0.32), 0 4px 10px rgba(3,218,196,0.2)',
                              '& .MuiSvgIcon-root': { color: 'white' },
                              '& .MuiTypography-root': { color: 'white' }
                            } : {
                              background: alpha(theme.palette.background.paper, 0.8),
                              '&:hover': {
                                background: `linear-gradient(135deg, ${alpha('#6200ee', 0.08)} 0%, ${alpha('#03dac4', 0.12)} 100%)`,
                                borderColor: alpha('#6200ee', 0.24),
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 16px rgba(98,0,238,0.16)'
                              }
                            })
                          }}
                        >
                          <Icon sx={{ fontSize: 20, color: active ? 'white' : 'text.secondary', transition: 'all 0.3s ease' }} />
                          <Typography sx={{ 
                            fontWeight: active ? 700 : 600, 
                            color: active ? 'white' : 'text.secondary',
                            transition: 'all 0.3s ease'
                          }}>
                            {typeof label === 'string' && label.startsWith('favorites') ? t(label) : label}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 4, border: '1px solid #e9e9e9' }}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems={isMobile ? 'stretch' : 'center'}>
              <TextField
                fullWidth
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('favorites.search', 'Rechercher dans mes favoris')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                value={city}
                onChange={(event) => setCity(event.target.value)}
                placeholder={t('favorites.city', 'Ville')}
                fullWidth={isMobile}
              />

              <FormControl sx={{ minWidth: isMobile ? '100%' : 220 }}>
                <InputLabel id="favorites-sort-label">{t('favorites.sort.label', 'Tri')}</InputLabel>
                <Select
                  labelId="favorites-sort-label"
                  label={t('favorites.sort.label', 'Tri')}
                  value={sort}
                  onChange={(event) => setSort(event.target.value)}
                  startAdornment={!isMobile ? (
                    <InputAdornment position="start">
                      <TuneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    </InputAdornment>
                  ) : null}
                >
                  {SORT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(option.label)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="text"
                onClick={() => {
                  setSearch('');
                  setCity('');
                  setSort('recent');
                }}
              >
                {t('favorites.reset', 'Réinitialiser')}
              </Button>
            </Stack>
          </Paper>

          {error && <Alert severity="error">{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Paper elevation={0} sx={{ p: { xs: 4, md: 6 }, borderRadius: 4, border: '1px dashed #d8d8d8', textAlign: 'center' }}>
              <FavoriteIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {t('favorites.empty.title', 'Aucun favori trouvé')}
              </Typography>
              <Typography color="text.secondary" sx={{ maxWidth: 520, mx: 'auto' }}>
                {t('favorites.empty.description', 'Ajoutez des cœurs depuis la recherche, les fiches listings/services ou les pages de commande produit pour retrouver vos éléments ici.')}
              </Typography>
            </Paper>
          ) : (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                    xl: 'repeat(3, minmax(0, 1fr))'
                  },
                  gap: 2
                }}
              >
                {items.map((item) => (
                  <SearchResultCard
                    key={item.id}
                    listing={item}
                    isProduct={favoriteType === 'product'}
                    layout="airbnb"
                    onSelect={() => handleCardSelect(item)}
                  />
                ))}
              </Box>

              {pagination?.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                  <Pagination
                    page={pagination.page}
                    count={pagination.totalPages}
                    color="primary"
                    onChange={(_, nextPage) => setPage(nextPage)}
                  />
                </Box>
              )}
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default FavoritesPage;